/**
 * Power-Step Modal Web Component
 * Displays when a product is added to cart with recommendations and cart total
 */

if (!customElements.get('power-step-modal')) {
    class PowerStepModal extends HTMLElement {
        constructor() {
            super();
        }

        connectedCallback() {
            this.overlay = this.querySelector('.power-step-modal__overlay');
            this.container = this.querySelector('.power-step-modal__container');
            this.closeButtons = this.querySelectorAll('.js-close-modal');
            this.addedProductSection = this.querySelector('#power-step-added-product');
            this.cartTotalSection = this.querySelector('#power-step-cart-total');
            this.recommendationsSection = this.querySelector('#power-step-recommendations');
            this.progressBarSection = this.querySelector('#power-step-progress-bar');

            this.bindEvents();
        }

        bindEvents() {
            // Close button clicks
            this.closeButtons.forEach(btn => {
                btn.addEventListener('click', () => this.close());
            });

            // ESC key to close
            this.keyupHandler = (evt) => {
                if (evt.key === 'Escape' && this.getAttribute('aria-hidden') === 'false') {
                    this.close();
                }
            };
            document.addEventListener('keyup', this.keyupHandler);

            // Listen for cart add events
            document.addEventListener('on:cart:add', (evt) => {
                this.handleCartAdd(evt.detail);
            });

            // Listen for checkout button click
            const checkoutBtn = this.querySelector('.js-checkout-button');
            if (checkoutBtn) {
                checkoutBtn.addEventListener('click', (evt) => {
                    evt.preventDefault();
                    window.location.href = '/checkout';
                });
            }

            // Listen for 'View Cart' button to open cart drawer
            const viewCartBtn = this.querySelector('.js-open-cart-drawer');
            if (viewCartBtn) {
                viewCartBtn.addEventListener('click', (evt) => {
                    evt.preventDefault();
                    this.close();
                    document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', { bubbles: true }));
                });
            }
        }

        async handleCartAdd(detail) {
            try {
                // Use cart from detail if available, otherwise fetch it
                let cartData = detail.cart;
                if (!cartData) {
                    const cartResponse = await fetch(`${theme.routes.cart}.js`);
                    cartData = await cartResponse.json();
                }

                // Sync fee product with cart
                cartData = await this.syncFeeWithCart(cartData);

                // Fetch the cart total section AFTER fee is ensured
                const sectionResponse = await fetch(`${theme.routes.cart}?sections=power-step-cart-total`);
                const sectionData = await sectionResponse.json();
                const sectionHtml = sectionData['power-step-cart-total'];

                // Find the added/updated product
                const addedItem = cartData.items.find(item => item.variant_id === detail.variantId);

                if (addedItem) {
                    this.open({
                        addedProduct: addedItem,
                        cart: cartData,
                        cartTotalHtml: sectionHtml
                    });
                }
            } catch (error) {
                console.error('Power-step modal error:', error);
            }
        }

        /**
         * Opens the modal
         * @param {object} data - Contains addedProduct and cart data
         */
        async open(data) {
            // Render added product
            this.renderAddedProduct(data.addedProduct);

            // Update cart total
            this.updateCartTotal(data.cartTotalHtml);

            // Sync Yaco progress bar from cart drawer
            this.syncYacoProgressBar();

            // Load recommendations
            await this.fetchHelloRetailRecommendations(data.cart, data.addedProduct);

            // Show modal
            this.setAttribute('aria-hidden', 'false');
            document.body.style.overflow = 'hidden';

            // Trap focus
            this.trapFocus();

            // Dispatch event
            this.dispatchEvent(new CustomEvent('power-step:opened', {
                bubbles: true,
                detail: data
            }));
        }

        /**
         * Syncs the fee product with the cart — always adds the fee if not present.
         * @param {object} cartData - Current cart data
         * @returns {Promise<object>} Updated cart data
         */
        async syncFeeWithCart(cartData) {
            const feeVariantIdRaw = theme.settings.feeVariantId;

            if (!feeVariantIdRaw) return cartData;

            // Skip fee on staging – the fee variant doesn't exist there
            if (window.location.hostname.includes('staging')) {
                console.info('[PowerStep] Staging detected – skipping fee sync');
                return cartData;
            }

            // Parse as number – Shopify requires numeric variant IDs
            const feeVariantId = Number(feeVariantIdRaw);
            if (isNaN(feeVariantId)) {
                console.error('Fee variant ID is not a valid number:', feeVariantIdRaw);
                return cartData;
            }

            const feeItem = cartData.items.find(item => item.variant_id === feeVariantId);

            // update.js sets qty (idempotent) instead of adding – prevents double fee on concurrent calls
            if (!feeItem || feeItem.quantity !== 1) {
                try {
                    const response = await fetch('/cart/update.js', {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'Accept': 'application/json'
                        },
                        body: JSON.stringify({
                            updates: { [feeVariantId]: 1 }
                        })
                    });

                    if (response.ok) {
                        const refreshedResponse = await fetch(`${theme.routes.cart}.js`);
                        const refreshedCart = await refreshedResponse.json();
                        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', { bubbles: true }));
                        return refreshedCart;
                    } else {
                        console.error('Failed to add fee product to cart', await response.json());
                    }
                } catch (error) {
                    console.error('Error adding fee product to cart:', error);
                }
            }

            return cartData;
        }

        /**
         * Closes the modal
         */
        close() {
            this.setAttribute('aria-hidden', 'true');
            document.body.style.overflow = '';

            // Return focus to trigger element
            if (this.opener) {
                this.opener.focus();
            }

            // Dispatch event
            this.dispatchEvent(new CustomEvent('power-step:closed', {
                bubbles: true
            }));
        }

        /**
         * Renders the added product section
         * @param {object} product - Cart line item
         */
        renderAddedProduct(product) {
            // Trim vendor from beginning of title if duplicated
            let title = product.product_title || '';
            if (product.vendor && title.toLowerCase().startsWith(product.vendor.toLowerCase())) {
                title = title.substring(product.vendor.length).replace(/^[\s\-–|:]+/, '').trim();
            }

            const html = `
        <div class="power-step-added-item">
          <div class="power-step-added-item__image">
            <a href="${product.url}" class="power-step-added-item__image-link">
                <img src="${product.featured_image.url}" alt="${product.featured_image.alt || product.product_title}" loading="lazy">
            </a>
          </div>
          <div class="power-step-added-item__details">
            <a href="${product.url}" class="power-step-added-item__title-link">
                ${product.vendor ? `<span class="power-step-added-item__brand">${product.vendor}</span>` : ''}
                <h3 class="power-step-added-item__title h6">${title}</h3>
            </a>
            ${product.variant_title ? `<p class="power-step-added-item__variant">${product.variant_title}</p>` : ''}
            <div class="power-step-added-item__bottom">
                <p class="power-step-added-item__price h6">${this.formatMoney(product.final_line_price)}</p>

                <div class="power-step-quantity-selector">
                    <button type="button" class="power-step-quantity-btn" name="minus" data-key="${product.key}" aria-label="Reduser antall"${product.quantity <= 1 ? ' disabled' : ''}>
                        <svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" role="presentation" class="icon icon-minus" fill="none" viewBox="0 0 10 2">
                            <path fill-rule="evenodd" clip-rule="evenodd" d="M.5 1C.5.7.7.5 1 .5h8a.5.5 0 110 1H1A.5.5 0 01.5 1z" fill="currentColor">
                        </svg>
                    </button>
                    <input class="power-step-quantity-input" type="number" name="quantity" value="${product.quantity}" min="1" aria-label="Antall" data-key="${product.key}">
                    <button type="button" class="power-step-quantity-btn" name="plus" data-key="${product.key}">
                        <svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" role="presentation" class="icon icon-plus" fill="none" viewBox="0 0 10 10">
                            <path fill-rule="evenodd" clip-rule="evenodd" d="M1 4.51a.5.5 0 000 1h3.5l.01 3.5a.5.5 0 001 0V5.51h3.5a.5.5 0 000-1H5.51V1a.5.5 0 00-1 0v3.51H1z" fill="currentColor">
                        </svg>
                    </button>
                </div>
            </div>
          </div>
        </div>
      `;
            this.addedProductSection.innerHTML = html;
            this.bindQuantityEvents();
        }

        bindQuantityEvents() {
            const container = this.addedProductSection.querySelector('.power-step-quantity-selector');
            if (!container) return;

            const input = container.querySelector('.power-step-quantity-input');
            const btns = container.querySelectorAll('.power-step-quantity-btn');

            btns.forEach(btn => {
                btn.addEventListener('click', (e) => {
                    e.preventDefault();
                    const isPlus = btn.getAttribute('name') === 'plus';
                    const key = btn.getAttribute('data-key');
                    let currentVal = parseInt(input.value);

                    if (isPlus) {
                        this.updateQuantity(key, currentVal + 1);
                    } else {
                        if (currentVal > 1) {
                            this.updateQuantity(key, currentVal - 1);
                        }
                    }
                });
            });

            input.addEventListener('change', (e) => {
                const key = input.getAttribute('data-key');
                let val = parseInt(input.value);
                if (val < 1) val = 1;
                this.updateQuantity(key, val);
            });
        }

        async updateQuantity(key, quantity) {
            try {
                this.addedProductSection.style.opacity = '0.7';

                const body = JSON.stringify({
                    id: key,
                    quantity: quantity,
                    sections: 'power-step-cart-total'
                });

                const response = await fetch(`${theme.routes.cartChange}`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/json'
                    },
                    body: body
                });

                const parsedState = await response.json();

                const updatedItem = parsedState.items.find(item => item.key === key || item.variant_id === parsedState.id);

                if (updatedItem) {
                    this.renderAddedProduct(updatedItem);
                    this.updateCartTotal(parsedState.sections['power-step-cart-total']);

                    document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
                        bubbles: true
                    }));
                    document.dispatchEvent(new CustomEvent('cart:updated', {
                        bubbles: true,
                        detail: {
                            cart: parsedState
                        }
                    }));

                    // Re-sync Yaco progress bar after cart update
                    setTimeout(() => this.syncYacoProgressBar(), 500);
                }

            } catch (error) {
                console.error('Error updating quantity:', error);
            } finally {
                this.addedProductSection.style.opacity = '1';
            }
        }

        updateCartTotal(html) {
            if (typeof html === 'string') {
                this.cartTotalSection.innerHTML = html;
            } else {
                console.error('updateCartTotal expects HTML string');
            }
        }

        /**
         * Clones the Yaco progress bar from the cart drawer into the powerstep
         */
        syncYacoProgressBar() {
            if (!this.progressBarSection) return;

            const yacoSource = document.querySelector('cart-drawer #yacoProgressBar');
            if (yacoSource) {
                const clone = yacoSource.cloneNode(true);
                clone.id = 'yacoProgressBarClone';
                this.progressBarSection.innerHTML = '';
                this.progressBarSection.appendChild(clone);
                this.progressBarSection.style.display = 'block';
            } else {
                // Yaco may not have rendered yet — retry once after a delay
                setTimeout(() => {
                    const yacoRetry = document.querySelector('cart-drawer #yacoProgressBar');
                    if (yacoRetry) {
                        const clone = yacoRetry.cloneNode(true);
                        clone.id = 'yacoProgressBarClone';
                        this.progressBarSection.innerHTML = '';
                        this.progressBarSection.appendChild(clone);
                        this.progressBarSection.style.display = 'block';
                    } else {
                        this.progressBarSection.style.display = 'none';
                    }
                }, 1000);
            }
        }

        /**
         * Fetches and renders Hello Retail recommendations.
         * Falls back to standard Shopify recommendations if HR fails.
         */
        async fetchHelloRetailRecommendations(cart, addedProduct) {
            try {
                let contextProductIds = [];
                if (addedProduct) {
                    contextProductIds = [Number(addedProduct.product_id)];
                } else if (cart.items.length > 0) {
                    contextProductIds = [Number(cart.items[0].product_id)];
                }

                if (contextProductIds.length === 0 || isNaN(contextProductIds[0])) {
                    this.recommendationsSection.style.display = 'none';
                    return;
                }

                // Staging override
                if (window.location.hostname.includes('staging')) {
                    console.info('[PowerStep] Staging – using debug product ID for Hello Retail');
                    contextProductIds = [9799282852179];
                }

                if (!window.HelloRetailService) {
                    console.error('HelloRetailService not loaded');
                    this.fetchStandardRecommendations(cart, addedProduct, this.recommendationsSection);
                    return;
                }

                const key = this.getAttribute('data-hr-key') || window.HelloRetailService?.powerstepKey;

                const result = await window.HelloRetailService.fetchRecommendations({
                    key: key,
                    awsKey: 'powerstep-bought-together',
                    productIds: contextProductIds,
                    count: 16,
                    filters: { inStock: "true" }
                });

                const products = result?.products;
                const hrTrackingKey = result?.trackingKey;

                if (!products || products.length === 0) {
                    this.fetchStandardRecommendations(cart, addedProduct, this.recommendationsSection);
                    return;
                }

                const container = this.recommendationsSection;
                if (!container) return;

                this.renderHRProductCards(products.slice(0, 16), container, hrTrackingKey);
                container.style.display = 'block';
                this.initSlider(container);

            } catch (error) {
                console.error('Error fetching Hello Retail recommendations:', error);
                this.fetchStandardRecommendations(cart, addedProduct, this.recommendationsSection);
            }
        }

        /**
         * Renders product cards directly from Hello Retail API data
         */
        renderHRProductCards(products, container, trackingKey) {
            const cardsHtml = products.map((product, cardIndex) => {
                const title = product.title || '';
                const url = product.url || '#';
                const imgUrl = product.imgUrl || '';
                const brand = product.brand || '';
                const variantId = product.extraData?.id || product['extraData.id'] || '';
                const price = product.price ? parseFloat(product.price) : 0;
                const oldPrice = product.oldPrice ? parseFloat(product.oldPrice) : 0;
                const isOnSale = oldPrice > 0 && oldPrice > price;

                const formatPrice = (val) => {
                    return val.toLocaleString('nb-NO', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                    }).replace(',00', '') + ' kr';
                };

                let priceHtml = '';
                if (isOnSale) {
                    const saving = oldPrice - price;
                    const savingFormatted = Math.round(saving).toLocaleString('nb-NO') + ' kr';
                    const savingBadge = saving >= 5
                        ? `<span class="mc-price-spar">Spar ${savingFormatted}</span>`
                        : '';
                    priceHtml = `
                        <span class="price price--on-sale">
                            <span class="mc-price-top"><span class="price__was">Veil. ${formatPrice(oldPrice)}</span></span>
                            <span class="mc-price-row"><span class="price__current">${formatPrice(price)}</span>${savingBadge}</span>
                        </span>
                    `;
                } else if (price > 0) {
                    priceHtml = `<span class="price"><span class="price__current">${formatPrice(price)}</span></span>`;
                }

                return `
                    <div class="hr-card-item${cardIndex >= 6 ? ' hr-card-item--extra' : ''}">
                        <a href="${url}" class="hr-card__image-link"
                            ${trackingKey ? `onclick="var h=window.hrq||[];h.push(['trackClick','${trackingKey}'])"` : ''}>
                            <div class="hr-card__image">
                                ${imgUrl
                        ? `<img src="${imgUrl}" alt="${title.replace(/"/g, '&quot;')}" loading="lazy">`
                        : `<div class="hr-card__placeholder">Ingen bilde</div>`
                    }
                            </div>
                        </a>
                        <div class="hr-card__right">
                            <a href="${url}" class="hr-card__info"
                                ${trackingKey ? `onclick="var h=window.hrq||[];h.push(['trackClick','${trackingKey}'])"` : ''}>
                                ${brand ? `<span class="hr-card__brand">${brand}</span>` : ''}
                                <span class="hr-card__title">${title}</span>
                            </a>
                            <div class="hr-card__bottom">
                                <div class="hr-card__prices">
                                    ${priceHtml}
                                </div>
                                <div class="card__quick-add card__quick-add--inline mob:card__quick-add--icon desktop:card__quick-add--icon">
                                    <product-form>
                                        <div class="alert mb-8 bg-error-bg text-error-text js-form-error text-start" role="alert" hidden></div>
                                        <form method="post" action="/cart/add" class="js-product-form" accept-charset="UTF-8" enctype="multipart/form-data" novalidate="novalidate">
                                            <input type="hidden" name="form_type" value="product">
                                            <input type="hidden" name="utf8" value="✓">
                                            <input type="hidden" name="id" value="${variantId}">
                                            <button class="btn btn--primary w-full" data-add-to-cart-text="Legg i handlekurv" name="add" aria-haspopup="dialog"
                                                ${trackingKey ? `data-tracking-key="${trackingKey}"` : ''}
                                                onclick="if(this.dataset.trackingKey){var h=window.hrq||[];h.push(['trackClick',this.dataset.trackingKey])}"
                                            >
                                                <span class="quick-add-btn-icon">
                                                    <span class="visually-hidden">Legg i handlekurv</span>
                                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false" role="presentation" class="icon">
                                                        <path d="M12.12 20.44H5.6V9.56h12.8v3.73c.06.4.4.69.8.7.44 0 .8-.35.8-.8v-4.5a.792.792 0 0 0-.8-.69H17V6.5C16.9 4 14.7 2 12 2S7 4.09 7 6.67V8H4.71c-.4.04-.71.37-.71.78v12.53a.8.8 0 0 0 .8.69h7.43c.38-.06.67-.39.67-.78 0-.43-.35-.78-.78-.78ZM8.66 6.67c0-1.72 1.49-3.11 3.33-3.11s3.33 1.39 3.33 3.11V8H8.65V6.67Z"></path>
                                                        <path d="M20 17.25h-2.4v-2.5a.817.817 0 0 0-.8-.7c-.44 0-.8.36-.8.8v2.4h-2.5c-.4.06-.7.4-.7.8 0 .44.36.8.8.8H16v2.5c.06.4.4.7.8.7.44 0 .8-.36.8-.8v-2.4h2.5c.4-.06.69-.4.7-.8 0-.44-.35-.8-.8-.8Z"></path>
                                                    </svg>
                                                </span>
                                                <span class="quick-add-btn-text">Legg i handlekurv</span>
                                            </button>
                                            <input type="hidden" name="product-id" value="${product.productNumber || ''}">
                                        </form>
                                    </product-form>
                                </div>
                            </div>
                        </div>
                    </div>
                `;
            });

            // Group cards into slides of 3
            const cards = cardsHtml;
            let slidesHtml = '';
            for (let i = 0; i < cards.length; i += 3) {
                const slideCards = cards.slice(i, i + 3).join('');
                slidesHtml += `<div class="hr-cards-slide">${slideCards}</div>`;
            }

            const showToggle = products.length > 6;
            container.innerHTML = `
                <h3 class="hr-cards-heading">Anbefalinger til handlekurven din</h3>
                <div class="hr-cards-grid" id="hr-cards-grid">
                    ${slidesHtml}
                </div>
                ${showToggle ? `
                <button type="button" class="hr-cards-toggle" id="hr-cards-toggle" aria-expanded="false">
                    Vis flere
                </button>` : ''}
            `;

            // Toggle expand/collapse
            if (showToggle) {
                const toggleBtn = container.querySelector('#hr-cards-toggle');
                const grid = container.querySelector('#hr-cards-grid');
                if (toggleBtn && grid) {
                    toggleBtn.addEventListener('click', () => {
                        const expanded = grid.classList.toggle('is-expanded');
                        toggleBtn.setAttribute('aria-expanded', expanded);
                        toggleBtn.textContent = expanded ? 'Vis færre' : 'Vis flere';
                    });
                }
            }
        }

        /**
         * Fetches and renders standard Shopify Product Recommendations as fallback
         */
        async fetchStandardRecommendations(cart, addedProduct, container) {
            try {
                let productId;
                if (addedProduct) {
                    productId = addedProduct.product_id;
                } else if (cart.items.length > 0) {
                    productId = cart.items[0].product_id;
                }

                if (!productId) {
                    container.style.display = 'none';
                    return;
                }

                const sectionId = 'power-step-recommendations-slider';
                const url = `${theme.routes.productRecommendations}?section_id=${sectionId}&product_id=${productId}&limit=16&intent=related`;

                const response = await fetch(url);
                if (!response.ok) throw new Error('Standard recommendations fetch failed');

                const text = await response.text();

                if (text.trim().length > 0) {
                    container.innerHTML = text;
                    container.style.display = 'block';
                    this.initSlider(container);
                } else {
                    container.style.display = 'none';
                }
            } catch (error) {
                console.error('Error fetching standard recommendations:', error);
                container.style.display = 'none';
            }
        }

        /**
         * Traps focus within the modal
         */
        trapFocus() {
            const focusableElements = this.querySelectorAll(
                'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
            );

            if (focusableElements.length === 0) return;

            const firstElement = focusableElements[0];
            const lastElement = focusableElements[focusableElements.length - 1];

            firstElement.focus();

            this.tabHandler = (evt) => {
                if (evt.key !== 'Tab') return;

                if (evt.shiftKey) {
                    if (document.activeElement === firstElement) {
                        evt.preventDefault();
                        lastElement.focus();
                    }
                } else {
                    if (document.activeElement === lastElement) {
                        evt.preventDefault();
                        firstElement.focus();
                    }
                }
            };

            this.addEventListener('keydown', this.tabHandler);
        }

        /**
         * Initializes slider navigation
         */
        initSlider(container) {
            const track = container.querySelector('.power-step-track');
            const prevBtn = container.querySelector('.power-step-prev');
            const nextBtn = container.querySelector('.power-step-next');
            const currentSlideEl = container.querySelector('.current-slide');
            const totalSlidesEl = container.querySelector('.total-slides');

            if (!track || !prevBtn || !nextBtn) return;

            const scrollAmount = track.clientWidth;

            prevBtn.addEventListener('click', () => {
                track.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
            });

            nextBtn.addEventListener('click', () => {
                track.scrollBy({ left: scrollAmount, behavior: 'smooth' });
            });

            track.addEventListener('scroll', () => {
                const scrollLeft = track.scrollLeft;
                const slideWidth = track.clientWidth;
                const currentIndex = Math.round(scrollLeft / slideWidth) + 1;

                if (currentSlideEl) {
                    currentSlideEl.textContent = currentIndex;
                }
            });
        }

        /**
         * Formats money
         * @param {number} cents - Amount in cents
         * @returns {string} Formatted money string
         */
        formatMoney(cents) {
            const amount = cents / 100;
            return `${amount.toFixed(2).replace('.', ',')} kr`;
        }

        disconnectedCallback() {
            document.removeEventListener('keyup', this.keyupHandler);
            if (this.tabHandler) {
                this.removeEventListener('keydown', this.tabHandler);
            }
        }
    }

    customElements.define('power-step-modal', PowerStepModal);
}
